import './App.css'; 
import React, { useState, useMemo, useEffect } from 'react';
import { BrowserRouter as Router } from "react-router-dom";
import { UserContext } from "./Components/UserContext";
import { ContextProvider } from './SocketContext';
import AlertPage from './Components/AlertPage';
import TopBarMobUI from './Components/MobUI/TopBarMobUI';
import FooterMobUI from './Components/MobUI/FooterMobUI';
import FooterUI from './Components/PC/FooterUI';
import NewNavbarUI from './Components/PC/NewNavbarUI';
import NewTopBarMobUI from './Components/MobUI/NewTopBarMobUI';
import PublicRoutes from './Components/Routes/PublicRoutes';
import PrivateRoutes from './Components/Routes/PrivateRoutes';
import { BroadcasterService } from "./Components/BroadcasterService";

const App = () => {
  const [user, setUser] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [alert, setAlert] = useState(null);
  const [open, setOpen] = React.useState(false);

// Create the flag at the top of your App component
  window.isIntentionalRedirect = false;

  const showAlert = (message, type) => {
    setAlert({ msg: message, type });
    setTimeout(() => setAlert(null), 3000);
  };

  const handleClick = () => setOpen(true);

  const handleClose = (event, reason) => {
    if (reason === 'clickaway') return;
    setOpen(false);
  };

  const value = useMemo(() => ({ user, setUser }), [user, setUser]);


  useEffect(() => {
    // 1. Intercept all global clicks to detect <a href="..."> clicks
    const handleGlobalClick = (e) => {
      // Find the nearest anchor (<a>) element if they clicked an icon/text inside a link
      const anchor = e.target.closest("a");
      
      // If it's a valid link and it doesn't open in a new tab (_blank), flag it as intentional
      if (anchor && anchor.href && anchor.target !== "_blank") {
        window.isIntentionalRedirect = true;
      }
    };

    // 2. Block Keyboard Shortcuts (F5, Ctrl+R, Cmd+R)
    const handleKeyDown = (e) => {
      if (
        e.key === "F5" || 
        (e.ctrlKey && (e.key === "r" || e.key === "R")) || 
        (e.metaKey && (e.key === "r" || e.key === "R"))
      ) {
        e.preventDefault();
      }
    };

    // 3. Warn only on true manual refreshes/closes
    const handleBeforeUnload = (e) => {
      if (window.isIntentionalRedirect) {
        // Reset the flag immediately after allowing the navigation
        // in case the user stops the navigation or it acts strangely
        setTimeout(() => {
          window.isIntentionalRedirect = false;
        }, 100);
        return; 
      }

      e.preventDefault();
      e.returnValue = ""; 
    };

    // Attach listeners
    window.addEventListener("click", handleGlobalClick, { capture: true });
    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("beforeunload", handleBeforeUnload);

    // Cleanup listeners on unmount
    return () => {
      window.removeEventListener("click", handleGlobalClick, { capture: true });
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  useEffect(() => {
    const storedUser = localStorage.getItem("loggedInUser");
    if (storedUser) setUser(JSON.parse(storedUser));

    setIsMobile(
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        window.navigator.userAgent
      )
    );
  }, []);

  const checkUserValidity = async () => {
    if (!user) return;
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/users/checkuservalidity/${user._id}`,
        {
          method: "GET",
          headers: { authtoken: localStorage.getItem("loggedintoken") },
        }
      );
      const data = res.status === 200 ? await res.json() : await res.text();
      if (data.data) {
        if (!data.data.active || data.data.accesstype !== user.accesstype) {
          window.logout(); // handlelogout();
        }
      }
    } catch (err) {
      console.log(err);
    }
  };

  const handlelogout = () => {
    localStorage.clear();
    setUser(null);
    window.location.href = "/";
  };

  return (
    <UserContext.Provider value={value}>
      <Router>
        {/* Broadcaster Service - Hidden */}
        {user && (Number(user.accesstype) < 5 /* broadcaster type */) && (
          <BroadcasterService user={user} />
        )}

        {(window.location.pathname === "/vacancies" || user) &&
        (!isMobile && user && !["c", "r"].includes(user.accesstype)) ? (
          <>
            <NewNavbarUI showAlert={showAlert} />
            <br /><br /><br />
          </>
        ) : user && isMobile && ["c", "r"].includes(user.accesstype) ? (
          <>
            <TopBarMobUI />
            <FooterMobUI />
          </>
        ) : user && !isMobile && ["c", "r"].includes(user.accesstype) ? (
          <FooterUI />
        ) : isMobile && user && !["c", "r"].includes(user.accesstype) ? (
          <NewTopBarMobUI />
        ) : (
          ""
        )}

        {alert && <AlertPage alert={alert} />}

        <ContextProvider>
          {!user || !user.accesstype ? (
            <PublicRoutes isMobile={isMobile} showAlert={showAlert} />
          ) : (
            <PrivateRoutes isMobile={isMobile} showAlert={showAlert} />
          )}
        </ContextProvider>
      </Router>
    </UserContext.Provider>
  );
};

export default App;
